<template>
  <div class="mt-6 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
    <h2 class="text-lg leading-6 font-medium text-gray-900 mb-3">
      {{ t("information") }}
    </h2>
    <form @submit.prevent="updateEquipment">
      <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
        <ui-input
          :invalid="'brand' in form.errors"
          :error="form.errors.brand"
          type="text"
          :label="t('brand')"
          v-model="form.brand"
        ></ui-input>

        <ui-input
          :invalid="'model' in form.errors"
          :error="form.errors.model"
          type="text"
          :label="t('model')"
          v-model="form.model"
        ></ui-input>

        <ui-input
          :invalid="'serial_number' in form.errors"
          :error="form.errors.serial_number"
          type="text"
          :label="t('serial_number')"
          v-model="form.serial_number"
        ></ui-input>

        <ui-input
          :invalid="'identifier' in form.errors"
          :error="form.errors.identifier"
          type="text"
          :label="t('identifier')"
          v-model="form.identifier"
        ></ui-input>

        <ui-input
          :invalid="'next_calibration_on' in form.errors"
          :error="form.errors.next_calibration_on"
          type="date"
          :label="t('next_calibration_on')"
          v-model="form.next_calibration_on"
          :cornerHint="t('optional')"
        ></ui-input>

        <div class="sm:col-span-2 flex justify-end">
          <ui-button
            :disabled="form.processing"
            class="w-full sm:w-auto flex items-center justify-center"
            type="submit"
          >
            {{ t("save") }}
          </ui-button>
        </div>
      </dl>
    </form>
    <h2 class="text-lg leading-6 font-medium text-gray-900 mt-6 mb-3">
      {{ t("timeline") }}
    </h2>
    <div class="flow-root mt-3">
      <ul class="-mb-8">
        <li>
          <div class="relative pb-8">
            <span
              v-if="equipment.data.equipmentStatusChanges.data.length"
              class="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
              aria-hidden="true"
            ></span>
            <Link
              class="relative flex items-start space-x-3"
              :href="
                route('equipment.equipment-status-changes.create', {
                  equipment: equipment.data.id,
                  search: $page.props.filters.search,
                })
              "
            >
              <div>
                <div class="relative px-1">
                  <div
                    class="
                      h-8
                      w-8
                      bg-gray-100
                      rounded-full
                      ring-8 ring-white
                      flex
                      items-center
                      justify-center
                    "
                  >
                    <PlusCircleIcon class="text-green-500 w-5 h-5" />
                  </div>
                </div>
              </div>
              <div class="min-w-0 flex-1 py-0">
                <div class="text-sm leading-8 text-gray-500">
                  <span class="mr-0.5 font-medium text-gray-900">
                    {{ t("change_status") }}
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </li>
        <li
          v-for="(equipmentStatusChange, index) in equipment.data
            .equipmentStatusChanges.data"
          :key="equipmentStatusChange.id"
        >
          <Link
            :href="
              route('equipment-status-changes.edit', {
                equipment_status_change: equipmentStatusChange.id,
                search: $page.props.filters.search,
              })
            "
            class="relative pb-8 block"
          >
            <span
              v-if="
                index < equipment.data.equipmentStatusChanges.data.length - 1
              "
              class="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
              aria-hidden="true"
            ></span>
            <div class="relative flex items-start space-x-3">
              <div>
                <div class="relative px-1">
                  <div
                    class="
                      h-8
                      w-8
                      bg-gray-100
                      rounded-full
                      ring-8 ring-white
                      flex
                      items-center
                      justify-center
                    "
                  >
                    <TagIcon class="w-5 h-5 text-gray-500" />
                  </div>
                </div>
              </div>
              <div class="min-w-0 flex-1 py-0">
                <div>
                  <div class="text-sm leading-8 text-gray-500">
                    <span class="mr-0.5">
                      <span href="#" class="font-medium text-gray-900">{{
                        equipmentStatusChange.user.name
                      }}</span>
                      {{ t("changed_status_to") }}
                    </span>
                    <span class="mr-0.5">
                      <span
                        href="#"
                        class="
                          relative
                          inline-flex
                          items-center
                          rounded-full
                          border border-gray-300
                          px-3
                          py-0.5
                          text-sm
                        "
                      >
                        <span
                          class="
                            absolute
                            flex-shrink-0 flex
                            items-center
                            justify-center
                          "
                        >
                          <span
                            :class="
                              'h-1.5 w-1.5 rounded-full ' +
                              statusColor(equipmentStatusChange.status)
                            "
                            aria-hidden="true"
                          ></span>
                        </span>
                        <span class="ml-3.5 font-medium text-gray-900">{{
                          t(equipmentStatusChange.status)
                        }}</span>
                      </span>
                    </span>
                    <span class="whitespace-nowrap">{{
                      equipmentStatusChange.diff_for_humans_created_at
                    }}</span>
                  </div>
                </div>
                <div class="text-sm text-gray-700">
                  <p>
                    {{ equipmentStatusChange.comments }}
                  </p>
                </div>
              </div>
            </div>
          </Link>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { PlusCircleIcon, TagIcon } from "@heroicons/vue/solid";
import { useI18n } from "vue-i18n";

export default {
  props: {
    equipment: { type: Object, required: true },
  },

  components: {
    PlusCircleIcon,
    TagIcon,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      form: this.$inertia.form({
        _method: "PUT",
        brand: this.equipment.data.brand,
        model: this.equipment.data.model,
        serial_number: this.equipment.data.serial_number,
        identifier: this.equipment.data.identifier,
        next_calibration_on: this.equipment.data.next_calibration_on,
      }),
    };
  },

  methods: {
    updateEquipment() {
      this.form.put(
        this.route("equipment.update", {
          equipment: this.equipment.data.id,
          search: this.$page.props.filters.search,
        }),
        {
          preserveScroll: true,
        }
      );
    },

    statusColor(status) {
      let colors = {
        available: "bg-green-400",
        occupied: "bg-light-blue-400",
        revision: "bg-yellow-400",
        calibration: "bg-yellow-400",
        unavailable: "bg-red-400",
      };

      if (status in colors) {
        return colors[status];
      }

      return "gray";
    },
  },

  watch: {
    customer(equipment, oldEquipment) {
      if (equipment.id != oldEquipment.id) {
        this.form.clearErrors();
        this.form.brand = this.equipment.data.brand;
        this.form.model = this.equipment.data.model;
        this.form.serial_number = this.equipment.data.serial_number;
        this.form.identifier = this.equipment.data.identifier;
        this.form.next_calibration_on = this.equipment.data.next_calibration_on;
      }
    },
  },
};
</script>

<i18n>
{
  "es": {
    "identifier": "Identificador",
    "brand": "Marca",
    "model": "Modelo",
    "serial_number": "Número de serie",
    "identifier": "Identificador",
    "next_calibration_on": "Siguiente calibración",
    "save": "Guardar",
    "optional": "opcional",
    "information": "Información",
    "timeline": "Línea del tiempo",
    "changed_status_to": "cambió el estatus del equipo a",
    "available": "Disponible",
    "unavailable": "Fuera de servicio",
    "revision": "En verificación",
    "calibration": "En calibración",
    "occupied": "En campo",
    "change_status": "Registrar nuevo estatus"
  }
}
</i18n>
